/**
 * Raw Material Order Interface Component
 * 
 * Matches backend raw material workflow:
 * 1. Show variations with reduction values and max quantities
 * 2. After selection, show quantity slider and input
 * 3. Send to cart functionality
 */

import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import QuantitySelector from './QuantitySelector';

const RawMaterialOrderInterface = ({ 
    productId, 
    _productInfo, 
    selectedVariation,
    _isGuest, 
    isMobile, 
    localization,
    onError,
    _onVariationSelect
}) => {
    const [loading, setLoading] = useState(false);
    const [rawMaterialInfo, setRawMaterialInfo] = useState([]);
    const [selectedRawMaterial, setSelectedRawMaterial] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [maxQuantity, setMaxQuantity] = useState(27);
    
    /**
     * Load raw material variations on component mount
     */
    useEffect(() => {
        loadRawMaterialInfo();
    }, [productId]);
    
    /**
     * Auto-select variation if one is already selected from ProductOrderInterface
     */
    useEffect(() => {
        if (selectedVariation && rawMaterialInfo.length > 0) {
            const matchingRawMaterial = rawMaterialInfo.find(
                rm => rm.variation_id === selectedVariation.variation_id
            );
            if (matchingRawMaterial) {
                handleVariationSelect(matchingRawMaterial);
            }
        }
    }, [selectedVariation, rawMaterialInfo]);
    
    /**
     * Load raw material variations from API
     */
    const loadRawMaterialInfo = async () => {
        setLoading(true);
        
        try {
            const response = await fetch(
                `${localization.apiUrl}product/${productId}/raw-material-info`,
                {
                    headers: {
                        'X-WP-Nonce': localization.nonce
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            setRawMaterialInfo(data);
            
        } catch (err) {
            onError(err, 'loadRawMaterialInfo');
        } finally {
            setLoading(false);
        }
    };
    
    /**
     * Handle variation selection
    */
    const handleVariationSelect = (rawMaterial) => {
        const computedMaxQty = Number(rawMaterial.variation_max_qty ?? 0);
        setSelectedRawMaterial(rawMaterial);
        setMaxQuantity(computedMaxQty);
        setQuantity(computedMaxQty > 0 ? 1 : 0);
    };
    
    /**
     * Handle quantity change from QuantitySelector
     */
    const handleQuantityChange = (newQuantity) => {
        // QuantitySelector already handles min/max validation
        setQuantity(newQuantity);
    };
    
    /**
     * Handle add to cart
     */
    const handleAddToCart = async () => {
        if (!selectedRawMaterial) {
            onError(__('請先選擇變體', 'frontend-order-system'), 'validation');
            return;
        }
        
        if (quantity <= 0) {
            onError(__('請設定數量', 'frontend-order-system'), 'validation');
            return;
        }
        
        setLoading(true);
        
        try {
            // Calculate stock_picks using nwsdao2 calculate_raw_material_shipment
            const shipmentResponse = await fetch(
                `${localization.apiUrl}calculate-raw-material-shipment`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': localization.nonce
                    },
                    body: JSON.stringify({
                        product_id: productId,
                        variation_id: selectedRawMaterial.variation_id,
                        required_qty: quantity,
                        info_data: rawMaterialInfo  // Pass all variation info for calculation
                    })
                }
            );
            
            if (!shipmentResponse.ok) {
                throw new Error(`Shipment calculation failed: ${shipmentResponse.status}`);
            }
            
            const shipmentResult = await shipmentResponse.json();
            
            if (!shipmentResult.success) {
                throw new Error(shipmentResult.error || __('庫存不足', 'frontend-order-system'));
            }
            
            // Transform nwsdao2 picks format to frontend format
            const transformedPicks = shipmentResult.picks.map(pick => ({
                stock_id: pick.cell_id || `${pick.drawer_name}-${pick.row_index}-${pick.col_index}`,
                qty: pick.pick_qty,
                drawer_name: pick.drawer_name,
                row_index: pick.row_index,
                col_index: pick.col_index,
                position: pick.position
            }));
            
            // Use transformed stock picks for cart
            const cartData = {
                product_id: productId,
                variation_id: selectedRawMaterial.variation_id,
                quantity: quantity,
                stock_picks: transformedPicks, // Use transformed picks from nwsdao2
                is_preorder: false
            };
            
            const response = await fetch(
                `${localization.apiUrl}add-to-cart`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': localization.nonce
                    },
                    body: JSON.stringify(cartData)
                }
            );
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                // Execute cart fragments JavaScript to update Flatsome mini-cart
                if (result.fragments_js) {
                    try {
                        const script = document.createElement('script');
                        script.textContent = result.fragments_js;
                        document.head.appendChild(script);
                        document.head.removeChild(script);
                    } catch (fragmentsError) {
                        // Silent fail for fragments JS execution
                    }
                }
                
                // Show success message but stay on product page
               // alert(__('成功加入購物車！', 'frontend-order-system'));

                // Reload raw material info to reflect cart consumption
                await loadRawMaterialInfo();

                // Reset selected variation to go back to variation selection view
                setSelectedRawMaterial(null);
                setQuantity(1);
            } else {
                throw new Error(result.data || __('加入購物車失敗', 'frontend-order-system'));
            }
            
        } catch (err) {
            onError(err, 'addToCart');
        } finally {
            setLoading(false);
        }
    };
    
    /**
     * Render loading state
     */
    if (loading && rawMaterialInfo.length === 0) {
        return (
            <div className="fos-raw-material-section">
                <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                    <div className="spinner" style={{ margin: '0 auto 10px' }}></div>
                    <span>{__('載入中...', 'frontend-order-system')}</span>
                </div>
            </div>
        );
    }
    
    /**
     * Render variation selection step (like Image 2)
     */
    if (!selectedRawMaterial) {
        return (
            <div className="fos-raw-material-section">
                <div style={{ padding: '0 20px 20px' }}>
                    <h3 style={{ 
                        margin: '0 0 20px', 
                        color: '#e65100',
                        fontSize: '1.1rem'
                    }}>
                        {__('可用變體', 'frontend-order-system')}
                    </h3>
                    
                    <div className="fos-raw-material-variations">
                        {rawMaterialInfo
                            .filter((rawMaterial) => {
                                const maxQty = Number(rawMaterial.variation_max_qty ?? 0);
                                return Number.isFinite(maxQty) && maxQty > 0;
                            })
                            .map((rawMaterial) => {
                                const maxQty = Number(rawMaterial.variation_max_qty ?? 0);
                                const safeMaxQty = Number.isFinite(maxQty) && maxQty >= 0 ? maxQty : 0;
                                
                                return (
                            <div 
                                key={rawMaterial.variation_id}
                                className="fos-raw-material-variation-card"
                                onClick={() => handleVariationSelect(rawMaterial)}
                                style={{
                                    padding: '16px 20px',
                                    margin: '0 0 12px',
                                    border: '2px solid #ffcc02',
                                    borderRadius: '8px',
                                    background: '#fff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = '#fff3e0';
                                    e.target.style.borderColor = '#ff8f00';
                                }}
                                onMouseLeave={(e) => {
                                   e.target.style.background = '#fff';
                                   e.target.style.borderColor = '#ffcc02';
                               }}
                           >
                                <div>
                                    <h4 style={{ 
                                        margin: '0 0 4px', 
                                        color: '#e65100',
                                        fontSize: '1.1rem'
                                    }}>
                                        {rawMaterial.variation_name}
                                    </h4>
                                    {rawMaterial.price && (
                                        <p style={{ 
                                            margin: '0', 
                                            color: '#666',
                                            fontSize: '0.9rem'
                                        }}>
                                            ${rawMaterial.price}
                                        </p>
                                    )}
                                </div>
                                <div style={{ textAlign: 'right', color: '#e65100' }}>
                                    <div style={{ fontSize: '0.85rem' }}>
                                        <span style={{ fontWeight: 600 }}>
                                            {__('最大數量:', 'frontend-order-system')}
                                        </span>{' '}
                                        <span>{safeMaxQty}</span>
                                    </div>
                                </div>
                            </div>
                        );
                        })}

                        {/* Show message if no variations available */}
                        {rawMaterialInfo.filter((rawMaterial) => {
                            const maxQty = Number(rawMaterial.variation_max_qty ?? 0);
                            return Number.isFinite(maxQty) && maxQty > 0;
                        }).length === 0 && (
                            <div style={{
                                padding: '40px 20px',
                                textAlign: 'center',
                                color: '#666',
                                border: '2px dashed #ccc',
                                borderRadius: '8px',
                                background: '#f9f9f9'
                            }}>
                                <div style={{ fontSize: '1.1rem', marginBottom: '8px' }}>
                                    {__('暫無可用庫存', 'frontend-order-system')}
                                </div>
                                <div style={{ fontSize: '0.9rem' }}>
                                    {__('所有變體庫存已用完，請稍後再試', 'frontend-order-system')}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    
    /**
     * Render quantity selection step (like Image 3)
     */
    return (
        <div className="fos-raw-material-section">
            <div style={{ padding: '0 20px 20px' }}>
                <h3 style={{ 
                    margin: '0 0 20px', 
                    color: '#e65100',
                    fontSize: '1.1rem'
                }}>
                    {__('可用變體', 'frontend-order-system')}
                </h3>
                
                {/* Selected variation display */}
                <div 
                    className="fos-raw-material-variation-card selected"
                    style={{
                        padding: '16px 20px',
                        margin: '0 0 20px',
                        border: '2px solid #2196f3',
                        borderRadius: '8px',
                        background: '#e3f2fd',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <div>
                        <h4 style={{ 
                            margin: '0 0 4px', 
                            color: '#1976d2',
                            fontSize: '1.1rem'
                        }}>
                            {selectedRawMaterial.variation_name}
                        </h4>
                        {selectedRawMaterial.price && (
                            <p style={{ 
                                margin: '0', 
                                color: '#666',
                                fontSize: '0.9rem'
                            }}>
                                ${selectedRawMaterial.price}
                            </p>
                        )}
                    </div>
                    <div style={{ textAlign: 'right', color: '#1976d2' }}>
                        <div style={{ fontSize: '0.85rem' }}>
                            <span style={{ fontWeight: 600 }}>
                                {__('最大數量:', 'frontend-order-system')}
                            </span>{' '}
                            <span>{Number(selectedRawMaterial.variation_max_qty ?? 0) >= 0 ? Number(selectedRawMaterial.variation_max_qty ?? 0) : 0}</span>
                        </div>
                    </div>
                </div>
                
                {/* Quantity selection */}
                <div className="fos-quantity-selection">
                    <h4 style={{ 
                        margin: '0 0 16px', 
                        color: '#e65100',
                        fontSize: '1rem'
                    }}>
                        {__('數量選擇', 'frontend-order-system')} - {selectedRawMaterial.variation_name}
                    </h4>
                    
                    {/* Quantity selector */}
                    {maxQuantity > 0 ? (
                        <div style={{ marginBottom: '20px' }}>
                            <QuantitySelector
                                quantity={quantity}
                                maxQuantity={maxQuantity}
                                minQuantity={1}
                                onChange={handleQuantityChange}
                                disabled={loading}
                                showQuickSelect={false}
                                showMaxLabel={false}
                                isMobile={isMobile}
                                size="normal"
                                style={{ marginBottom: '16px' }}
                            />
                        </div>
                    ) : (
                        <div style={{ 
                            padding: '16px',
                            background: '#ffebee',
                            border: '1px solid #f44336',
                            borderRadius: '4px',
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            <strong style={{ color: '#d32f2f' }}>
                                ⚠️ {__('此變體目前無庫存', 'frontend-order-system')}
                            </strong>
                        </div>
                    )}
                    
                    {/* Send to cart button */}
                    <button
                        onClick={handleAddToCart}
                        disabled={loading || maxQuantity === 0 || quantity === 0}
                        style={{
                            width: '100%',
                            padding: '12px 24px',
                            background: (maxQuantity === 0) ? '#bdbdbd' : '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '1.1rem',
                            fontWeight: '500',
                            cursor: (loading || maxQuantity === 0 || quantity === 0) ? 'not-allowed' : 'pointer',
                            opacity: (loading || maxQuantity === 0) ? 0.6 : 1,
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            if (!loading && maxQuantity > 0) {
                                e.target.style.background = '#45a049';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!loading && maxQuantity > 0) {
                                e.target.style.background = '#4caf50';
                            }
                        }}
                    >
                        {loading ? (
                            <>
                                <span className="spinner" style={{ marginRight: '8px' }}></span>
                                {__('加入購物車...', 'frontend-order-system')}
                            </>
                        ) : (
                            <>
                                🛒 {__('加入購物車', 'frontend-order-system')}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RawMaterialOrderInterface;
